import * as React from "react"
import * as AML from "models/aml";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Link } from "react-scroll";
import { FilterContext } from "models/core/filter";
import { DashChip } from "components/common/DashChips";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { Download, Downloading } from "@mui/icons-material";
import { AMLIndicatorDefinitionsUrl } from "config/appUrlConfig";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum AMLIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: AMLIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: AMLIndicatorAction.DownloadIndicatorSuccess }
	| { type: AMLIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number
}

const initialState: State = {
	indicator: 0
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case AMLIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload } };

		case AMLIndicatorAction.DownloadIndicatorSuccess:
		case AMLIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0 } };

		default:
			return state;
	}
}

let stateWideData: {
	title?: string;
	patients?: string;
	apmlPatients?: string;
	bmatBeforeFirstTreat?: string;
	flt3BeforeFirstTreat?: string;
	hadIVST?: string;
	firstTreat0To5Days?: string;
	firstTreat6To10Days?: string;
	firstTreat11To15Days?: string;
	firstTreatMoreThan15Days?: string;
	hadBMT?: string;
	bmt0To3Months?: string;
	bmt3To6Months?: string;
	bmt6To9Months?: string;
	bmt9To12Months?: string;
	lengthOfStayBMT?: string;
	oneYearSurvival?: string;
	twoYearSurvival?: string;
	fiveYearSurvival?: string;
	mortality30Days?: string;
	mortality31To365Days?: string;
} = {};


export function AMLDashboard() {
	const [localState, localDispatch] = React.useReducer(reducer, initialState)
	const { state, dispatch } = React.useContext(AppContext);


	const [showFilterChips, setFilterChips] = React.useState(true);

	const addFilter: AML.AddFilterFunc = async (key: keyof typeof AML.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await AML.computeChartData(AML.addFilter(state.page.dashboard as AML.IAMLDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: AML.RemoveFilterFunc = async (key: keyof typeof AML.DashboardFilterKeys, value?: string) => {
		const filteredDashboard = await AML.computeChartData(AML.removeFilter(state.page.dashboard as AML.IAMLDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		const fetchStateReferenceData = async () => {
			stateWideData = await getStateIndicatorReferences("aml");
		}

		const loadDashboard = async () => {
			const value = await AML.loadAMLDashboard(state.page.dashboard as AML.IAMLDashboard);
			dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value });
		}

		Promise.all([fetchStateReferenceData(), loadDashboard()]);
		return () => {
			AML.closeAMLDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = AMLChartData.level1.filteredReferralIdCsv;
		let alldata = false;


		const getAMLIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getAMLIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: AMLIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: AMLIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: AMLIndicatorAction.DownloadIndicatorFail })
			}
		}

		getAMLIndicatorPatientList();


	}, [localState.indicator]);


	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in AML.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(AML.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": AML.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const AMLChartData = state.page.dashboard.chartData as AML.IChartData;



	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: AMLIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}

	const ecog = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	const stage = {
		numerator: 0,
		denominator: 0,
		percentage: 0
	}

	if (AMLChartData.level1.totalDiagnoses > 0) {
		ecog.numerator = AMLChartData.level1.totalDiagnoses - (AMLChartData.level2.ecogGroup.values[AMLChartData.level2.ecogGroup.labels.indexOf("Unk")] || 0);
		ecog.denominator = AMLChartData.level1.totalDiagnoses
		ecog.percentage = ecog.numerator / ecog.denominator * 100

		stage.numerator = AMLChartData.level1.totalDiagnoses - (AMLChartData.level2.tnmStage.values[AMLChartData.level2.tnmStage.labels.indexOf("Unk")] || 0);
		stage.denominator = AMLChartData.level1.totalDiagnoses;
		stage.percentage = stage.numerator / stage.denominator * 100
	}


	const dashboard = <>
		<div style={{
			position: "sticky",
			top: "64px",
			left: "10px",
			zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>



		<div style={{
			maxWidth: 1000,
			margin: "0 auto"
		}}>

			<div style={{
				textAlign: "right",
				paddingBottom: "10px",
				paddingTop: "10px",
				position: "sticky",
				zIndex: 1,
				top: "60px",
				backgroundColor: "white"
			}}>
				<Button onClick={() => localDispatch({ type: AMLIndicatorAction.UserClickedDownloadIndicator, payload: 1 })}
					style={{
						backgroundColor: (AMLChartData.level1.totalDiagnoses > 0 ? ("#1e88e5") : ("#d3d3d3")),
						color: "white",
						alignItems: "right"
					}}>
					Download Patient Details
				</Button>
			</div>
			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell align="center">My MDT(s)</TableCell>
							<TableCell align="center">{stateWideData.title}*</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Primary AML (excluding APML) diagnoses*</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.totalDiagnoses}`}</TableCell>
							<TableCell align="center">{stateWideData.patients}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>APML diagnoses*</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.hadAPML}`}</TableCell>
							<TableCell align="center">{stateWideData.apmlPatients}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Percentage of patients with ECOG</TableCell>
							<TableCell align="center">{`${ecog.percentage.toFixed(0)}%`} <br /> {`(${ecog.numerator}/${ecog.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						{/* <TableRow>
							<TableCell colSpan={4}>
								<b>Diagnosis</b>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received bone marrow aspirate and trephine (BMAT) biopsy prior to first treatment</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.bmatBeforeFirstTreat.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.bmatBeforeFirstTreat.numerator}/${AMLChartData.level1.bmatBeforeFirstTreat.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.bmatBeforeFirstTreat}</TableCell>
						</TableRow>

                        <TableRow>
							<TableCell>Received FLT3 test prior to first treatment</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.flt3BeforeFirstTreat.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.flt3BeforeFirstTreat.numerator}/${AMLChartData.level1.flt3BeforeFirstTreat.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.flt3BeforeFirstTreat}</TableCell>

						</TableRow> */}

						<TableRow>
							<TableCell colSpan={4}>
								<b>Systemic therapy</b>
							</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Had IV systemic therapy</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.hadIVST.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.hadIVST.numerator}/${AMLChartData.level1.hadIVST.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadIVST}</TableCell>

						</TableRow>
						<TableRow>
							<TableCell>Received 1st treatment within 0-5 days of diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.firstTreat0To5Days.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.firstTreat0To5Days.numerator}/${AMLChartData.level1.firstTreat0To5Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.firstTreat0To5Days}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received 1st treatment within 6-10 days of diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.firstTreat6To10Days.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.firstTreat6To10Days.numerator}/${AMLChartData.level1.firstTreat6To10Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.firstTreat6To10Days}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received 1st treatment within 11-15 days of diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.firstTreat11To15Days.percentage.toFixed(1)}%`} <br /> {`(${AMLChartData.level1.firstTreat11To15Days.numerator}/${AMLChartData.level1.firstTreat11To15Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.firstTreat11To15Days}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received 1st treatment more than 15 days from diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.firstTreatMoreThan15Days.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.firstTreatMoreThan15Days.numerator}/${AMLChartData.level1.firstTreatMoreThan15Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.firstTreatMoreThan15Days}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Received bone marrow transplant</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.hadBMT.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.hadBMT.numerator}/${AMLChartData.level1.hadBMT.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadBMT}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Received BMT within 0-&lt;3 months of  diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.bmt0To3Months.percentage.toFixed(1)}%`} <br /> {`(${AMLChartData.level1.bmt0To3Months.numerator}/${AMLChartData.level1.bmt0To3Months.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.bmt0To3Months}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received BMT within 3-&lt;6 months of  diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.bmt3To6Months.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.bmt3To6Months.numerator}/${AMLChartData.level1.bmt3To6Months.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.bmt3To6Months}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received BMT within 6-&lt;9 months of  diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.bmt6To9Months.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.bmt6To9Months.numerator}/${AMLChartData.level1.bmt6To9Months.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.bmt6To9Months}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received BMT within 9-&lt;12 months of  diagnosis</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.bmt9To12Months.percentage.toFixed(1)}%`} <br /> {`(${AMLChartData.level1.bmt9To12Months.numerator}/${AMLChartData.level1.bmt9To12Months.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.bmt9To12Months}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Length of stay for bone marrow transplant (days)</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.lengthOfStayBMT.median}`} <br /> {`(${AMLChartData.level1.lengthOfStayBMT.min} - ${AMLChartData.level1.lengthOfStayBMT.max})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.lengthOfStayBMT}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>One year survival^</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.oneYearSurvival.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.oneYearSurvival.numerator}/${AMLChartData.level1.oneYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.oneYearSurvival}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>Two year survival^</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.twoYearSurvival.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.twoYearSurvival.numerator}/${AMLChartData.level1.twoYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.twoYearSurvival}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Five year survival^</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.fiveYearSurvival.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.fiveYearSurvival.numerator}/${AMLChartData.level1.fiveYearSurvival.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.fiveYearSurvival}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>30 day mortality from first treatment</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.mortality30Days.percentage.toFixed(1)}%`} <br /> {`(${AMLChartData.level1.mortality30Days.numerator}/${AMLChartData.level1.mortality30Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30Days}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>31-365 day mortality from first treatment</TableCell>
							<TableCell align="center">{`${AMLChartData.level1.mortality31To365Days.percentage.toFixed(0)}%`} <br /> {`(${AMLChartData.level1.mortality31To365Days.numerator}/${AMLChartData.level1.mortality31To365Days.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality31To365Days}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>


			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Patients in MDT include all diagnoses that have been presented to MDT and no exclusions have been made.
					In contrast, the report presents the incidence case of the AML diagnosis and in addition, de novo cases.
					Therefore, exclusions have been made based on previous AML diagnosis, prior systemic therapy and/or radiotherapy treatment,
					diagnosis of myeloproliferative disorder, aplastic anaemia and/or myelodysplastic syndrome.
					Rate of APML diagnosis has been presented but excluded from all other analysis.
					
					Data based on the facility of MDT</i> </p>
			</div>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>^Patients with less than one, two, and five years of data from diagnosis are excluded from the one, two, and five year survival indicators respectively.</i></p>
			</div>

			<div style={{ textAlign: "left" }}>
				<p>This report is based on the <a href={AMLIndicatorDefinitionsUrl} target="_blank">Queensland AML Cancer Quality Index 2011-2020.</a> Indicator definitions and glossary can be found here also</p>
			</div>

		</div>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)


} 